/*
 *  -----------------------------------------------------------------------
 *    <copyright file="observation-detail-template/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React from 'react';
import { useNavigate } from 'react-router';
import cn from 'clsx';
import moment from 'moment';
import ActionsMenuV2 from '../../core/actions-menu-v2';
import { IActionItemV2 } from '../../core/expandable/grid-v2/interfaces-v2/grid-props-v2.interface';
import NavigationService from '@components-clean/page-nav/navigation.service';
import { CentralMonitoringObservationInput_GridList } from '@interfaces/cm-observations.interface';
import { RiskObservationFields_GridList } from '@interfaces/risk-observation.interface';
import { useTranslation } from '@hooks/index';
import { generateUpdatedField, generateCreatedField } from '@utils/generator';
import './styles.scss';
import { AllRoutes } from '@utils/router/all-routes';
import { StudyFields } from '@queries/generated/fragments.generated';

interface IObservationDetail {
    observation: RiskObservationFields_GridList | CentralMonitoringObservationInput_GridList | any;
    editObservation: (value: any) => any;
    canDelete?: boolean;
    canEdit?: boolean;
    isRA?: boolean;
    handleRestoreObs?: Function;
    handleDeleteObs?: Function;
    showDeletedItems?: boolean;
    study?: StudyFields;
}

const GetDate = ({ date }: any) => <span>{moment(date).format('DD-MMM-YYYY')}</span>;

const ObservationDetailTemplate = ({
    observation,
    editObservation,
    canDelete,
    canEdit,
    isRA = true,
    handleRestoreObs,
    handleDeleteObs,
    showDeletedItems,
    study,
}: IObservationDetail) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const rowActions: IActionItemV2[] = observation.deleted
        ? [
              {
                  label: 'cm:restoreObservation',
                  icon: ['fal', 'undo'],
                  onClick: () => handleRestoreObs(observation),
                  enabled: canEdit,
              },
          ]
        : [
              {
                  label: 'ra:editObservation',
                  icon: ['fal', 'edit'],
                  onClick: () => editObservation(observation),
                  enabled: canEdit,
              },
              ...(canDelete
                  ? [
                        {
                            label: 'cm:deleteObservation',
                            icon: ['fal', 'trash'],
                            onClick: () => handleDeleteObs(observation),
                            enabled: canEdit,
                        } as IActionItemV2,
                    ]
                  : []),
              ...(!isRA
                  ? [
                        {
                            label: 'cm:viewSiteDashboard',
                            icon: ['far', 'eye'],
                            onClick: () => {
                                NavigationService.setViewLegacySiteDashboard();
                                navigate(AllRoutes.path('cm-site-dashboard') as string, {
                                    state: {
                                        skipSelectDate: true,
                                        showDashboard: true,
                                        siteId: observation._site.id as string | null | undefined,
                                    },
                                });
                            },
                            enabled: observation._siteDeleted === false && observation._site,
                        },
                    ]
                  : []),
          ];

    return (
        <div
            className={cn('observation-detail-wrapper', { 'observation-detail-wrapper--deleted': observation.deleted })}
        >
            <div className="menu-wrapper-item left">
                <ActionsMenuV2
                    height={41}
                    actions={rowActions}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                />
            </div>
            <div className="content-field">
                <div className="main-side">
                    <div className="observation-column-fields">
                        {showDeletedItems && observation.deleted && (
                            <>
                                <div>
                                    <dl data-testid="deleted">
                                        <dt>{t('ra:deleted')}:</dt>
                                        <dd>{t('yes')}</dd>
                                    </dl>
                                    <dl data-testid="deletedBy">
                                        <dt>{t('ra:deletedBy')}:</dt>
                                        <dd>{observation._deletedBy}</dd>
                                    </dl>
                                    <dl data-testid="deletedDate">
                                        <dt>{t('ra:deletedDate')}:</dt>
                                        <dd>{moment(observation._deleteDate).utc().format('DD-MMM-YYYY')}</dd>
                                    </dl>
                                </div>
                                <dl data-testid="reasonForDeletion">
                                    <dt>{t('ra:reasonForDeletion')}:</dt>
                                    <dd>{observation._deleteReason}</dd>
                                </dl>
                            </>
                        )}
                        <dl data-testid="observation">
                            <dt>{t('ra:observation')}:</dt>
                            <dd>{observation.description}</dd>
                        </dl>
                        {observation.potentialCause && (
                            <dl data-testid="potentialCause">
                                <dt>{t('ra:potentialCause')}:</dt>
                                <dd>{observation.potentialCause}</dd>
                            </dl>
                        )}
                        {observation.confirmedCause && (
                            <dl data-testid="confirmedCause">
                                <dt>{t('ra:confirmedCause')}:</dt>
                                <dd>{observation.confirmedCause}</dd>
                            </dl>
                        )}
                        {observation.comments && (
                            <dl data-testid="comments">
                                <dt>{t('ra:comments')}:</dt>
                                <dd>{observation.comments}</dd>
                            </dl>
                        )}
                        <div className="divider" />
                    </div>
                    <div className="short-text-container">
                        {observation.classification && (
                            <dl data-testid="classification">
                                <dt>{t('ra:classification')}:</dt>
                                <dd>{observation.classification.name}</dd>
                            </dl>
                        )}
                        <dl data-testid="recordId">
                            <dt>{t('ra:id')}:</dt>
                            <dd>{observation.recordId}</dd>
                        </dl>
                        {observation.observationDate && (
                            <dl data-testid="observationDate">
                                <dt>{t('ra:observationDate')}:</dt>
                                <dd>
                                    <GetDate date={observation.observationDate} />
                                </dd>
                            </dl>
                        )}
                        {observation._owner && (
                            <dl data-testid="owner">
                                <dt>{t('owner')}:</dt>
                                <dd>{observation._owner.text}</dd>
                            </dl>
                        )}
                        {observation.closedDate && (
                            <dl data-testid="closedDate">
                                <dt>{isRA ? t('ra:closedDate') : t('cm:closedDatePopulatedOnDate')}:</dt>
                                <dd>
                                    <GetDate date={observation.closedDate} />
                                </dd>
                            </dl>
                        )}
                        {observation._responsibleFunction && (
                            <dl data-testid="responsibleFunction">
                                <dt>{t('ra:responsibleFunction')}:</dt>
                                <dd>{observation._responsibleFunction.name}</dd>
                            </dl>
                        )}
                        {observation._recentActionDate && (
                            <dl data-testid="recentAction">
                                <dt>{t('ra:recentAction')}:</dt>
                                <dd>
                                    <GetDate date={observation._recentActionDate} />
                                </dd>
                            </dl>
                        )}
                        <dl data-testid="openActions">
                            <dt>{t('ra:openActions')}:</dt>
                            <dd>
                                {' '}
                                {observation._openAction}/{observation.actions?.length}
                            </dd>
                        </dl>
                        {observation._site && !isRA && (
                            <dl data-testid="site">
                                <dt>{t('cm:site')}:</dt>
                                <dd>{observation._site?.name}</dd>
                            </dl>
                        )}
                        {observation._datasetDate && !isRA && (
                            <dl data-testid="datasetDate">
                                <dt>{t('cm:datasetDate')}:</dt>
                                <dd>
                                    <GetDate date={observation._datasetDate} />
                                </dd>
                            </dl>
                        )}
                        {observation._panelName && !isRA && (
                            <dl data-testid="panelName">
                                <dt>{t('cm:panelName')}</dt>
                                <dd>{observation._panelName}</dd>
                            </dl>
                        )}
                        {isRA && (
                            <dl data-testid="qtlExceeded">
                                <dt>{t('ra:qtlExceeded')}:</dt>
                                <dd>{t(`common:${observation.qtlExceeded ? 'yes' : 'no'}`)}</dd>
                            </dl>
                        )}
                    </div>
                    <div className="divider no-mb" />
                    <div className="created-updated-wrap">
                        <p className="created-text">{generateCreatedField(observation)}</p>
                        <p className="created-text flex-end">{generateUpdatedField(observation)}</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ObservationDetailTemplate;
